// frontend/src/components/tablekit/FilterToolbar.jsx
import * as React from 'react';
import { Box, Button, FormControl, MenuItem, Select, TextField, IconButton, Chip, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { COL_TYPES, OPERATORS, normalizeType } from './filters';

export default function FilterToolbar({
  columns,
  filters,
  addFilter,
  updateFilter,
  removeFilter,
  clearFilters,
  dense = true,
}) {
  const [newColKey, setNewColKey] = React.useState(columns?.[0]?.key || '');
  const currentCol = React.useMemo(
    () => columns.find(c => c.key === newColKey) || columns[0],
    [newColKey, columns]
  );
  const colType = normalizeType(currentCol);
  const ops = OPERATORS[colType] || [];

  const [newOp, setNewOp] = React.useState(ops?.[0]?.key || '');
  const [newValue, setNewValue] = React.useState('');

  React.useEffect(() => {
    // refresh operators when column changes
    const freshOps = OPERATORS[normalizeType(currentCol)] || [];
    setNewOp(freshOps[0]?.key || '');
    setNewValue('');
  }, [currentCol]);

  const handleAdd = () => {
    // operators that don't need a value
    const noValue = ['isEmpty', 'isNotEmpty'].includes(newOp);
    addFilter({
      columnKey: currentCol.key,
      op: newOp,
      value: noValue ? '' : newValue,
    });
    setNewValue('');
  };

  const valueInput = (() => {
    // no-value ops
    if (['isEmpty', 'isNotEmpty'].includes(newOp)) return null;

    if (colType === COL_TYPES.number) {
      return (
        <TextField
          type="number"
          size={dense ? 'small' : 'medium'}
          label="Value"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          sx={{ minWidth: 120 }}
        />
      );
    }
    if (colType === COL_TYPES.date) {
      return (
        <TextField
          type="date"
          size={dense ? 'small' : 'medium'}
          label="Date"
          InputLabelProps={{ shrink: true }}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          sx={{ minWidth: 160 }}
        />
      );
    }
    return (
      <TextField
        size={dense ? 'small' : 'medium'}
        label="Text"
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        sx={{ minWidth: 200 }}
      />
    );
  })();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {/* Builder */}
      <FormControl size={dense ? 'small' : 'medium'} sx={{ minWidth: 160 }}>
        <Select value={newColKey} onChange={(e) => setNewColKey(e.target.value)}>
          {columns.map(c => (
            <MenuItem key={c.key} value={c.key}>{c.header ?? c.key}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size={dense ? 'small' : 'medium'} sx={{ minWidth: 140 }}>
        <Select value={newOp} onChange={(e) => setNewOp(e.target.value)}>
          {(OPERATORS[normalizeType(currentCol)] || []).map(op => (
            <MenuItem key={op.key} value={op.key}>{op.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {valueInput}
      <Button size={dense ? 'small' : 'medium'} startIcon={<AddIcon />} variant="outlined" onClick={handleAdd}>
        Add Filter
      </Button>

      {/* Current filters as chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
        {filters.map((f, i) => {
          const col = columns.find(c => c.key === f.columnKey);
          const label = `${col?.header ?? f.columnKey} ${f.op}${f.value ? `: ${f.value}` : ''}`;
          return (
            <Chip
              key={`f-${i}`}
              label={label}
              onDelete={() => removeFilter(i)}
              deleteIcon={<DeleteIcon />}
              variant="outlined"
              size="small"
            />
          );
        })}
        <Tooltip title="Clear all filters">
          <span>
            <Button size="small" color="secondary" onClick={clearFilters} disabled={!filters.length}>
              Clear Filters
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
